import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import Cookies from 'universal-cookie';
import CustomTypography from '../../components/Typography/CustomTypography';
import WrapperContainer from '../../components/Container/WrapperContainer';
import GridItem from '../../components/Grid/GridItem';
import Validation from '../../common/Validation';
import CleanableInput from '../../components/Input/CleanableInput';
import GridContainer from '../../components/Grid/GridContainer';
import CustomButton from '../../components/Button/CustomButton';
import { APIErrors, OBJ_LANGUAGE, Role } from '../../common/Constants';
import { VisibilityIcon, VisibilityOffIcon } from '../../components/Icons/Icon';
import InputAdornment from '../../components/Input/InputAdornment';

import styles from '../../assets/jss/pages/userLoginStyle';
import Loader from '../../components/Loader/Loader';

const useStyles = makeStyles(styles);
const UserLogin = (props) => {
    const {
        fetchLogin, errors, isAuthorized, credential, isLoading, eventInfo, getCustomText, clearCustomText, customText, locale, changeLanguage,
    } = props;
    const classes = useStyles(props);
    const history = useHistory();
    const routeParams = useParams();
    const intl = useIntl();
    const cookies = new Cookies();
    const [validateResults, setValidateResults] = useState({});
    const [validateRole, setValidateRole] = useState({});
    const [permission, setPermission] = useState(false);
    const [user, setuser] = useState({ email: '', password: '', showPass: 'password' });
    const target = window.location.pathname.split('/')[3];
    const roleNoLogin = window.location.pathname.indexOf('/register-exhibitor') >= 0
    || window.location.pathname.indexOf('/exhibitor_login') >= 0;

    useEffect(() => {
        if (roleNoLogin) {
            getCustomText({
                eventShortName: routeParams.shortName,
                site: 1,
                text_type: 4,
                language: locale,
            });
        } else {
            getCustomText({
                eventShortName: routeParams.shortName,
                site: 0,
                text_type: 6,
                language: locale,
            });
        }

        return () => {
            clearCustomText();
        };
    }, [locale]);

    useEffect(() => {
        if (isAuthorized && credential.role !== Role.admin) {
            if (credential.role === Role.viewer) {
                if (credential.language && credential.language !== 'ja' && credential.language !== 'en') {
                    sessionStorage.setItem('selectedLang', OBJ_LANGUAGE[credential.language]);
                    cookies.set('googtrans', `/en/${credential.language}`, { path: '/' });
                    history.push(`/${routeParams.shortName}/home`);
                    window.location.reload();
                } else {
                    sessionStorage.setItem('selectedLang', credential.language === 'en' ? 'English' : '日本語');
                    changeLanguage(credential.language);
                    history.push(`/${routeParams.shortName}/home`);
                    window.location.reload();
                }
            } else if (credential.role === Role.organizer) {
                changeLanguage('ja');
                history.push(`/${routeParams.shortName}/organizer/home`);
                window.location.reload();
            }
        }
    }, [isAuthorized, credential.role]);
    useEffect(() => {
        if (permission && credential.role) {
            if (credential.role !== Role.organizer && credential.role !== Role.viewer) {
                setValidateRole({ ...validateRole, message: { result: 'error', message: intl.formatMessage({ id: 'common.noPermission' }) } });
            }
        }
    }, [permission, credential.role]);

    useEffect(() => {
        if (!_.isEmpty(errors)) {
            if (errors.code === APIErrors.badRequest) {
                setValidateRole({});
                setValidateResults({ ...validateResults, message: errors.message, email: errors.email });
            }
        }
    }, [errors]);

    const checkTime7Day = (date) => {
        const utcDateNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
        utcDateNow.setHours(0, 0, 0, 0);
        const timeRunningStart = new Date(date);
        const time7Day = new Date(timeRunningStart.setDate(timeRunningStart.getDate() - 7));
        time7Day.setHours(0, 0, 0, 0);
        return !(time7Day < utcDateNow);
    };

    const onClickLogin = () => {
        const { email, password } = user;
        setValidateResults({});
        if (validate()) {
            fetchLogin({
                email,
                uuid: password,
                event_name: routeParams.shortName,
                language: locale,
            });
            setPermission(true);
        }
    };

    const validate = () => {
        const { email, password } = user;
        const validateRules = {
            email: [() => Validation.isNotEmpty(email),
                () => Validation.validateMaxLength(email, 255),
                () => Validation.validateEmail(email)],
            password: [() => Validation.isNotEmpty(password),
                () => Validation.validateMaxLength(password, 255),
            ],
        };
        const validateResult = Validation.validateAllRules(validateRules);

        if (Object.keys(validateResult).length > 0) {
            setValidateResults({ ...validateResult });
            window.scrollTo(0, 0);
            return false;
        }
        setValidateResults({});
        return true;
    };

    const handleSetParams = useCallback((field, valueChange) => {
        setuser((prevParams) => ({ ...prevParams, [field]: valueChange }));
    }, []);
    const handleSetShowPass = useCallback(() => {
        const valueshow = user.showPass === 'password' ? 'text' : 'password';
        setuser((prevParams) => ({ ...prevParams, showPass: valueshow }));
    });

    return (
        <div className={classes.wrapper}>
            {!isLoading && (
                <WrapperContainer>
                    <GridItem xs={12} md={12}>
                        <div className={roleNoLogin ? classes.background : classes.backgroundLoginViewer}>
                            <GridContainer>
                                <GridItem xs={12} sm={12} md={12} className={classes.mainBox}>
                                    <Box className={classes.box}>
                                        {
                                            !!validateResults.message && (
                                                <CustomTypography color="danger" variant="subtitle1">
                                                    {validateResults.message.message}
                                                </CustomTypography>
                                            )
                                        }
                                        {
                                            !!validateRole.message && (
                                                <CustomTypography color="danger" variant="subtitle1">
                                                    {validateRole.message.message}
                                                </CustomTypography>
                                            )
                                        }
                                        <CustomTypography
                                            component="h1"
                                            variant="h4"
                                            color="info"
                                            bold
                                            style={{ paddingBottom: '35px' }}
                                            className={`${classes.title}${classes.item}`}
                                            customColor={!roleNoLogin && eventInfo?.settings_colors_viewer?.screen_name_text_color}
                                        >
                                            {target && target === 'visitor_login'
                                                ? (eventInfo?.settings_texts_sidebar_viewer?.screen_name?.visitor_login
                                                    || intl.formatMessage({ id: 'userRouters.visitorLogin' }))
                                                : (eventInfo?.settings_texts_sidebar_organizer?.screen_name?.exhibitor_login
                                                    || intl.formatMessage({ id: 'userRouters.exhibitorLogin' }))}
                                        </CustomTypography>
                                        <CustomTypography
                                            className={classes.item}
                                            required
                                            bold
                                            customColor={!roleNoLogin && eventInfo?.settings_colors_viewer?.contents_text_color}
                                        >
                                            {customText?.field_name?.email || intl.formatMessage({ id: 'mailAddress' })}
                                        </CustomTypography>
                                        <CleanableInput
                                            classes={{ root: !roleNoLogin && classes.colorTextInput }}
                                            className={classes.item}
                                            placeholder={
                                                intl.formatMessage({ id: 'login.email' },
                                                    { email: customText?.field_name?.email || intl.formatMessage({ id: 'mailAddress' }).toLocaleLowerCase() })
                                            }
                                            defaultValue={user.email}
                                            onChange={(value) => handleSetParams('email', value)}
                                            error={!!validateResults.email}
                                            helperText={validateResults.email
                                                ? intl.formatMessage(
                                                    { id: validateResults.email.message },
                                                    { maxLength: 255 },
                                                ) : ''}
                                        />
                                        <CustomTypography
                                            className={classes.item}
                                            required
                                            bold
                                            customColor={!roleNoLogin && eventInfo?.settings_colors_viewer?.contents_text_color}
                                        >
                                            {intl.formatMessage({ id: 'login.password' })}
                                        </CustomTypography>
                                        <InputAdornment
                                            classes={{ root: !roleNoLogin && classes.colorTextInput }}
                                            className={`${classes.input} ${classes.item}`}
                                            type={user.showPass}
                                            size="small"
                                            placeholder={intl.formatMessage({ id: 'login.notePassword' })}
                                            defaultValue={user.password}
                                            onChange={(value) => handleSetParams('password', value)}
                                            error={!!validateResults.password}
                                            helperText={validateResults.password
                                                ? intl.formatMessage(
                                                    { id: validateResults.password.message },
                                                    { maxLength: 255 },
                                                ) : ''}
                                            onClick={() => handleSetShowPass()}
                                        >
                                            <>
                                                {user.showPass === 'password' && <VisibilityIcon />}
                                                {user.showPass === 'text' && <VisibilityOffIcon />}
                                            </>
                                        </InputAdornment>
                                        <Box justifyContent="center" display="flex">
                                            <CustomButton
                                                size="md"
                                                round
                                                color="info"
                                                gradient
                                                onClick={onClickLogin}
                                                style={{ margin: '20px', minWidth: '140px', fontSize: '20px' }}
                                                buttonTextColor={!roleNoLogin && eventInfo?.settings_colors_viewer?.button_text_color}
                                                buttonBackgroundColor={!roleNoLogin && eventInfo?.settings_colors_viewer?.button_background_color?.viewer}
                                            >
                                                {target && target === 'visitor_login'
                                                    ? intl.formatMessage({ id: 'home.loginViewer' })
                                                    : intl.formatMessage({ id: 'home.loginExhibit' })}
                                            </CustomButton>
                                        </Box>
                                        {
                                            target && target === 'visitor_login' && (
                                                <div className={classes.linkRegister}>
                                                    <a
                                                        href={`${window.location.origin}/${process.env.REACT_APP_ROOT_PATH}/${routeParams.shortName}/register-visitor`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {intl.formatMessage({ id: 'login.guidelineViewer' })}
                                                    </a>
                                                </div>
                                            )
                                        }
                                        {
                                            checkTime7Day(eventInfo.sdate) && target && target === 'exhibitor_login' && (
                                                <div className={classes.linkRegister}>
                                                    <a
                                                        href={`${window.location.origin}/${process.env.REACT_APP_ROOT_PATH}/${routeParams.shortName}/register-exhibitor`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {intl.formatMessage({ id: 'login.guidelineOrganizer' })}
                                                    </a>
                                                </div>
                                            )
                                        }
                                    </Box>
                                </GridItem>
                            </GridContainer>
                        </div>
                    </GridItem>
                </WrapperContainer>
            )}
            <Loader isLoading={isLoading} />
        </div>
    );
};
export default UserLogin;

UserLogin.defaultProps = {
    fetchLogin: () => { },
    errors: {},
    isAuthorized: false,
    credential: {},
    eventInfo: {},
    customText: {},
    getCustomText: () => {},
    clearCustomText: () => {},
    locale: 'ja',
    changeLanguage: () => {},
};

UserLogin.propTypes = {
    fetchLogin: PropTypes.func,
    errors: PropTypes.oneOfType([() => null, PropTypes.object]),
    credential: PropTypes.oneOfType([() => null, PropTypes.object]),
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
    isAuthorized: PropTypes.bool,
    isLoading: PropTypes.bool.isRequired,
    getCustomText: PropTypes.func,
    clearCustomText: PropTypes.func,
    customText: PropTypes.oneOfType([() => null, PropTypes.object]),
    locale: PropTypes.string,
    changeLanguage: PropTypes.func,
};
